import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { consumePlpScrollTarget } from '@theme/plp-scroll-target';

const RESTORING_CLASS = 'plp-restoring';
const FETCH_CONCURRENCY = 5;
// Y offset the scroll-spy samples the "top-most visible product" at — sits below
// the sticky header/filter bar. Tune if your sticky chrome is taller/shorter.
const SPY_FALLBACK_OFFSET = 120;

/**
 * Append-only paginated product grid (collection + search).
 *
 * Invariant: the DOM always holds one contiguous run of pages [firstPage..lastPage],
 * and we only ever APPEND at the bottom — never prepend — so there is no scroll
 * compensation anywhere. The URL is driven by the viewport (scroll-spy + replaceState),
 * not by what's loaded, so PLP→PDP→Back lands on the right page. Back-restore rebuilds
 * pages 1..N invisibly ("manual BFCache") and jumps to the product.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement}  [viewMoreNext]
 * @property {HTMLButtonElement}[loadMoreButton]
 * @property {HTMLElement} [productCountCurrent]
 * @property {HTMLElement} [productCountTotal]
 * @property {HTMLElement} [productCountProgress]
 * @property {HTMLElement} [productCountProgressBar]
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */ #pages = new Map();
  /** @type {number} */ #firstPage = 1;
  /** @type {number} */ #lastPage = 1;
  /** @type {number} */ #lastKnownPage = 1;
  /** @type {number} */ #pageSize = 0;
  /** @type {number} */ #landingPage = 1;
  /** @type {number} */ #landingOffset = 0;
  /** @type {'infinite' | 'load-more'} */ #mode = 'infinite';
  /** @type {boolean} */ #isLoading = false;
  /** @type {boolean} */ #scrollSpyScheduled = false;
  /** @type {IntersectionObserver | undefined} */ #nextObserver;
  /** @type {PaginatedListAspectRatioHelper | undefined} */ #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    const { grid } = this.refs;
    if (!grid) return;

    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });

    this.#readConfig();
    this.#syncPageRangeFromDom();
    this.#landingPage = this.#firstPage;
    this.#landingOffset = Number(grid.dataset.currentOffset) || 0;

        // The inline anti-flash script armed a 3s safety reveal in case this module
    // never loads. It has loaded — cancel it so it can't reveal mid-restore.
    // Restore owns the reveal now, and may legitimately take >3s on a big collection.
    clearTimeout(window.__plpRestoreSafetyTimer);

    // Restore runs first and fully, with the grid hidden, so the forward trigger
    // and scroll-spy never observe intermediate states. Then wire everything up.
    this.#restoreScrollTarget().finally(() => {
      if (!this.isConnected) return;
      this.#wireForwardTrigger();
      this.#wireScrollSpy();
      this.#prefetch(this.#lastPage + 1);
    });

    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener(ThemeEvents.FilterRender, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#nextObserver?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener(ThemeEvents.FilterRender, this.#handleFilterUpdate);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  // ---- config / state ------------------------------------------------------

  #readConfig() {
    const { grid, loadMoreButton } = this.refs;
    this.#lastKnownPage = Number(grid?.dataset.lastPage) || 1;
    this.#pageSize = Number(grid?.dataset.pageSize) || 0;
    this.#mode = loadMoreButton ? 'load-more' : 'infinite';
  }

  #syncPageRangeFromDom() {
    const pages = this.#domPages();
    const fallback = this.#pageFromUrl();
    this.#firstPage = pages.length ? Math.min(...pages) : fallback;
    this.#lastPage = pages.length ? Math.max(...pages) : fallback;
  }

  /** @returns {number[]} distinct page numbers currently in the grid. */
  #domPages() {
    const { grid } = this.refs;
    if (!grid) return [];
    const set = new Set();
    for (const card of grid.querySelectorAll(':scope > [data-page]')) {
      const p = Number(card.dataset.page);
      if (!Number.isNaN(p)) set.add(p);
    }
    return [...set];
  }

  #pageFromUrl() {
    const p = Number(new URL(location.href).searchParams.get('page'));
    return Number.isNaN(p) || p < 1 ? 1 : p;
  }

  // ---- restore (manual BFCache) -------------------------------------------

  async #restoreScrollTarget() {
    const target = consumePlpScrollTarget();
    if (!target?.productId) {
      document.documentElement.classList.remove(RESTORING_CLASS); // reveal plain landings
      return;
    }

    const { grid } = this.refs;
    if (!grid) return;

    document.documentElement.classList.add(RESTORING_CLASS); // belt & suspenders

    try {
      const targetPage = Number(target.page) || this.#pageFromUrl();
      const maxPage = Math.min(Math.max(targetPage, this.#lastPage), this.#lastKnownPage);

      const have = new Set(this.#domPages());
      const missing = [];
      for (let p = 1; p <= maxPage; p++) if (!have.has(p)) missing.push(p);

      await this.#fetchPagesConcurrent(missing, FETCH_CONCURRENCY);

      const incoming = [];
      for (const p of missing) {
        const cards = this.#getCardsForPage(p);
        if (cards) incoming.push(...cards);
      }
      const newItems = this.#dedupe(grid, incoming);
      if (newItems.length) await this.#appendInChunks(grid, newItems, 8);

      await this.#ensurePromoTiles();

      this.#firstPage = 1;
      this.#lastPage = maxPage;
      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      const card = grid.querySelector(`[data-product-id="${CSS.escape(target.productId)}"]`);
      await this.#settleToTarget(card); // re-pin through late media shifts, still hidden
    } catch (error) {
      console.error('PLP restore failed:', error);
    } finally {
      await this.#nextFrames(1); // let the scroll paint before we reveal
      document.documentElement.classList.remove(RESTORING_CLASS);
    }
  }

  /** @param {number[]} pageNumbers @param {number} limit */
  async #fetchPagesConcurrent(pageNumbers, limit) {
    let i = 0;
    const worker = async () => {
      while (i < pageNumbers.length) await this.#fetchSpecificPage(pageNumbers[i++]);
    };
    await Promise.all(Array.from({ length: Math.min(limit, pageNumbers.length) }, worker));
  }

  // ---- forward loading -----------------------------------------------------

  #wireForwardTrigger() {
    if (this.#mode === 'load-more') {
      const { loadMoreButton } = this.refs;
      loadMoreButton?.removeEventListener('click', this.#onLoadMoreClick);
      loadMoreButton?.addEventListener('click', this.#onLoadMoreClick);
      return;
    }
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;
    this.#nextObserver?.disconnect();
    this.#nextObserver = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) this.#loadNextPage(); },
      { rootMargin: '600px 0px' }
    );
    this.#nextObserver.observe(viewMoreNext);
  }

  #onLoadMoreClick = () => {
    const { loadMoreButton } = this.refs;
    loadMoreButton?.classList.add('loading');
    if (loadMoreButton instanceof HTMLButtonElement) loadMoreButton.disabled = true;
    this.#loadNextPage().finally(() => {
      const btn = this.refs.loadMoreButton;
      if (!btn) return;
      btn.classList.remove('loading');
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
  };

  async #loadNextPage() {
    if (this.#isLoading) return;
    if (this.#lastPage >= this.#lastKnownPage) { this.#removeLoadMoreButton(); return; }

    this.#isLoading = true;
    const page = this.#lastPage + 1;
    try {
      if (!this.#pages.has(page)) await this.#fetchSpecificPage(page);
      const { grid } = this.refs;
      const cards = this.#getCardsForPage(page);
      if (!grid || !cards) return;

      const newItems = this.#dedupe(grid, cards);
      if (newItems.length) {
        if (this.#mode === 'load-more') await this.#appendInChunks(grid, newItems);
        else grid.append(...newItems);
      }
      this.#lastPage = page;
      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      if (this.#lastPage >= this.#lastKnownPage) this.#removeLoadMoreButton();
      this.#prefetch(page + 1);
    } catch (error) {
      console.error('Failed to load next page:', error);
    } finally {
      this.#isLoading = false;
      if (this.#mode === 'infinite') this.#maybeContinueInfinite();
    }
  }

  /** Keeps filling when the sentinel is still in range (short pages / tall viewports). */
  #maybeContinueInfinite() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext || this.#lastPage >= this.#lastKnownPage) return;
    if (viewMoreNext.getBoundingClientRect().top <= window.innerHeight + 600) {
      requestAnimationFrame(() => this.#loadNextPage());
    }
  }

  #removeLoadMoreButton() {
    this.refs.loadMoreButton?.closest('.load-more-container')?.remove();
  }

  #prefetch(page) {
    if (page < 1 || page > this.#lastKnownPage || this.#pages.has(page)) return;
    requestIdleCallback(() => { this.#fetchSpecificPage(page); });
  }

  /** @param {Element} parent @param {Element[]} nodes @param {number} [chunkSize] */
  async #appendInChunks(parent, nodes, chunkSize = 6) {
    if (nodes.length <= chunkSize) { parent.append(...nodes); return; }
    for (let i = 0; i < nodes.length; i += chunkSize) {
      parent.append(...nodes.slice(i, i + chunkSize));
      if (i + chunkSize < nodes.length) {
        await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      }
    }
  }

  // ---- fetch / parse -------------------------------------------------------

  async #fetchSpecificPage(page) {
    if (this.#pages.has(page) || page < 1 || page > this.#lastKnownPage) return;
    const url = new URL(location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    try {
      this.#pages.set(page, await sectionRenderer.getSectionHTML(this.sectionId, false, url));
    } catch (error) {
      console.error(`Failed to fetch page ${page}:`, error);
    }
  }

  /** @param {number} page @returns {Element[] | null} */
  #getCardsForPage(page) {
    const html = this.#pages.get(page);
    if (!html) return null;
    const gridEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');
    return gridEl ? [...gridEl.querySelectorAll(':scope > [ref="cards[]"]')] : null;
  }

  async #ensurePromoTiles() {
    const { grid } = this.refs;
    if (!grid || grid.querySelector(':scope > .product-grid__item-banner')) return;
    if (!this.#pages.has(1)) await this.#fetchSpecificPage(1);
    const html = this.#pages.get(1);
    if (!html) return;
    const gridEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');
    const tiles = gridEl ? [...gridEl.querySelectorAll(':scope > .product-grid__item-banner')] : [];
    if (tiles.length) { grid.append(...tiles); this.#aspectRatioHelper?.processNewElements(); }
  }

  /** @param {Element} grid @param {Element[]} items @returns {Element[]} */
  #dedupe(grid, items) {
    const seen = new Set(
      [...grid.querySelectorAll(':scope > [data-product-id]')].map((el) => el.getAttribute('data-product-id'))
    );
    return items.filter((el) => {
      const id = el.getAttribute('data-product-id');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  /**
   * Resolves after `n` animation frames — lets custom elements upgrade and
   * layout settle before we measure or scroll.
   *
   * @param {number} n
   * @returns {Promise<void>}
   */
  #nextFrames(n) {
    return new Promise((resolve) => {
      const step = (remaining) =>
        remaining <= 0 ? resolve() : requestAnimationFrame(() => step(remaining - 1));
      step(n);
    });
  }

    /**
   * Keeps the target product centred while late layout shifts settle (product
   * media reserving height as it loads). Re-centres each frame until the scroll
   * position stops changing, or a max timeout — all while the grid is still
   * hidden, so none of the shifting is visible.
   *
   * @param {Element | null} card
   * @param {number} [maxMs]
   */
  async #settleToTarget(card, maxMs = 2500) {
    if (!card) return;
    const start = performance.now();
    let stableFrames = 0;
    let lastY = -1;
    while (performance.now() - start < maxMs && stableFrames < 8) {
      card.scrollIntoView({ behavior: 'instant', block: 'center' });
      await this.#nextFrames(1);
      const y = Math.round(window.scrollY);
      if (Math.abs(y - lastY) <= 1) stableFrames += 1;
      else stableFrames = 0;
      lastY = y;
    }
  }

  // ---- scroll-spy (URL driver) --------------------------------------------

  #wireScrollSpy() {
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#syncUrlToViewport();
  }

  #onScroll = () => {
    if (this.#scrollSpyScheduled) return;
    this.#scrollSpyScheduled = true;
    requestAnimationFrame(() => { this.#scrollSpyScheduled = false; this.#syncUrlToViewport(); });
  };

  #syncUrlToViewport() {
    const el = document.elementFromPoint(Math.round(window.innerWidth / 2), this.#spyOffset());
    const page = el?.closest?.('[data-page]')?.dataset.page;
    if (!page) return;
    const url = new URL(location.href);
    if ((url.searchParams.get('page') || '1') === page) return;
    if (page === '1') url.searchParams.delete('page');
    else url.searchParams.set('page', page);
    history.replaceState(history.state, '', url.toString());
  }

    /**
   * The Y offset the scroll-spy samples at — just below the sticky chrome
   * (header + announcement marquee). Read live so a dismissed marquee or a
   * resize is handled automatically. Falls back to a constant if the vars
   * aren't present.
   *
   * @returns {number}
   */
  #spyOffset() {
    const cs = getComputedStyle(document.documentElement);
    const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const px = (name) => {
      const raw = cs.getPropertyValue(name).trim();
      if (!raw) return 0;
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return 0;
      // --header-height is typically px; handle rem/em just in case.
      return raw.endsWith('rem') || raw.endsWith('em') ? n * rootFont : n;
    };
    const chrome = px('--header-height') + px('--marquee-height');
    return (chrome || SPY_FALLBACK_OFFSET) + 8; // 8px buffer to land on the first row below
  }

  // ---- count ---------------------------------------------------------------

  #updateProductCount() {
    const { grid, productCountCurrent, productCountTotal, productCountProgress, productCountProgressBar } = this.refs;
    if (!grid) return;
    const total = Number(productCountTotal?.textContent) || 0;
    const domCount = grid.querySelectorAll(':scope > [data-product-id]').length;
    // Pages below the loaded run are assumed full (only the last page is partial).
    // Use Shopify's exact offset while we're still on the landing page; otherwise derive it.
    const offset = this.#firstPage === this.#landingPage && this.#landingOffset
      ? this.#landingOffset
      : this.#pageSize ? (this.#firstPage - 1) * this.#pageSize : 0;
    let current = offset + domCount;
    if (total > 0) current = Math.min(current, total);

    if (productCountCurrent) productCountCurrent.textContent = String(current);
    const percent = total > 0 ? Math.min(100, (current / total) * 100) : 0;
    if (productCountProgressBar) productCountProgressBar.style.width = `${percent}%`;
    if (productCountProgress) productCountProgress.setAttribute('aria-valuenow', String(current));
  }

  // ---- filter updates / bfcache -------------------------------------------

  /**
   * Re-syncs pagination state after a filter/sort change. Listens on
   * ThemeEvents.FilterRender rather than FilterUpdate — that event fires the instant
   * the user changes a filter, well before the section re-render (a network request)
   * has landed, which used to force this into racing a MutationObserver against a
   * fixed 3s timeout: too slow a render (slow connection, big collection) and the
   * timeout won unconditionally, leaving #pages/#lastPage stale forever. FilterRender
   * only fires once the new grid has actually morphed into the DOM, so this can just
   * run directly — no waiting, no guessing, and no dependency on the page count having
   * changed (a sort change, for example, never changes the page count at all, which
   * used to make the old MutationObserver's "did data-last-page change?" check skip
   * reinitializing even when it ran in time).
   */
  #handleFilterUpdate = () => {
    if (!this.refs.grid) return;
    this.#pages.clear();
    this.#readConfig();
    this.#syncPageRangeFromDom();
    this.#landingPage = this.#firstPage;
    this.#landingOffset = Number(this.refs.grid?.dataset.currentOffset) || 0;
    this.#updateProductCount();
    this.#wireForwardTrigger();
    this.#prefetch(this.#lastPage + 1);
  };

  #handlePageShow = (event) => {
    // @ts-ignore - persisted exists on pageshow events.
    if (!event?.persisted) return; // only if BFCache ever comes back (Cloudflare removed)
    this.#nextObserver?.disconnect();
    this.#readConfig();
    this.#syncPageRangeFromDom();
    this.#updateProductCount();
    this.#wireForwardTrigger();
  };
}